// =============================================================================
// Puente de impresión: usa el comando nativo de Tauri cuando corre como app de
// escritorio (caja), y cae a la impresión del navegador (PDF/diálogo) en web.
// El mismo SPA sirve para ambos; aquí se decide el camino en runtime.
// =============================================================================

/** true si corremos dentro de la app nativa Tauri (caja con hardware). */
export function isNative(): boolean {
  // Tauri inyecta __TAURI_INTERNALS__ en window.
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

export interface PrinterConfig {
  transport: "network" | "windows";
  target: string; // "192.168.1.50:9100" o nombre de impresora
}

export interface Receipt {
  content: string;     // tiquete ya compuesto (texto plano, líneas con \n)
  openDrawer: boolean; // abrir gaveta (venta en efectivo)
  cut: boolean;
}

/** Imprime un tiquete. En nativo va a la impresora ESC/POS + abre gaveta;
 *  en web abre el diálogo de impresión del navegador. */
export async function printReceipt(cfg: PrinterConfig, r: Receipt): Promise<void> {
  if (isNative()) {
    const { invoke } = await import("@tauri-apps/api/core");
    await invoke("print_receipt_cmd", {
      job: { transport: cfg.transport, target: cfg.target, content: r.content, open_drawer: r.openDrawer, cut: r.cut },
    });
    return;
  }
  // Fallback web: imprimir el contenido como documento simple.
  printInBrowser(r.content);
}

/** Abre solo la gaveta (apertura manual autorizada). Solo nativo. */
export async function openDrawer(cfg: PrinterConfig): Promise<void> {
  if (!isNative()) throw new Error("Abrir gaveta requiere la app de caja (versión de escritorio).");
  const { invoke } = await import("@tauri-apps/api/core");
  await invoke("open_drawer_cmd", { transport: cfg.transport, target: cfg.target });
}

function printInBrowser(content: string) {
  const w = window.open("", "_blank", "width=320,height=600");
  if (!w) return;
  w.document.write(`<pre style="font:12px JetBrains Mono,monospace;white-space:pre-wrap">${escapeHtml(content)}</pre>`);
  w.document.close();
  w.focus();
  w.print();
}

function escapeHtml(s: string) {
  return s.replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]!));
}
